import { createContext, useContext, useMemo, useRef, useState, Dispatch, MutableRefObject, ReactNode, SetStateAction } from 'react';
import { Dayjs } from 'dayjs';

// Kanal-täckning för urvalet: masks[bit] = antal medlemmar med exakt den kombinationen av kanaler.
// Bit 1 = e-post, bit 2 = Kivra, bit 4 = brev (postnummer). Räknas i Medlemmar-steget, används i Leveransmetoder.
export interface SelectionCoverage {
  // total = antal FAKTUROR (mottagare). Vid samlingsfaktura slås hushållsmedlemmar ihop under huvudmedlemmen, så
  // total = medlemmar utom hushållsmedlemmar. Kanal-täckning (masks) och kostnad räknas på denna mottagar-nivå.
  total: number;
  // Antal MEDLEMMAR som omfattas (personer) – oberoende av samlingsfaktura. Visas i previewen bredvid antalet fakturor.
  membersCovered: number;
  masks: number[]; // längd 8
  approx: boolean;
  kivraAvailable: boolean;
}

export interface ProfileFilter {
  groupId: number;
  groupTitle: string;
  itemId: number;
  itemName: string;
}

export interface CountryBreakdown {
  country: string;
  count: number;
  email: number;
  pnr: number;
  post: number;
}

export interface SelectionBreakdown {
  approx: boolean;
  countries: CountryBreakdown[];
}

// Cache av hela urvals-resultatet (per urvals-nyckel) så vi inte gör om medlems-/Kivra-anropen i onödan.
export interface SelectionResultCache {
  key: string;
  count: number;
  breakdown: SelectionBreakdown;
  coverage: SelectionCoverage;
}

export const CH_EMAIL = 1;
export const CH_KIVRA = 2;
export const CH_LETTER = 4;

// Steg 4 (Texter) består av interna flikar (E-post, Fakturans innehåll, Fakturablankett, Förhandsgranska). Steget
// registrerar sin flik-navigering här så wizard-footerns Tillbaka/Fortsätt kan bläddra mellan flikarna innan man
// går till föregående/nästa steg. Är hasTabs=false (t.ex. "kompletteras senare") beter sig footern som vanligt.
export interface TextStepNav {
  hasTabs: boolean;
  atFirstTab: boolean;
  atLastTab: boolean;
  // Fortsätt får aktiveras (aktuell flik klarmarkerad, eller man står på Förhandsgranska-fliken).
  canForward: boolean;
  goNextTab: () => void;
  goPrevTab: () => void;
}

interface SendingWizardContextValue {
  coverage: SelectionCoverage | null;
  setCoverage: (c: SelectionCoverage | null) => void;
  // Aktiv urvals-metod (tab): 0 = eget urval, 1 = fritext. (Kopiera från avisering är numera en modal.)
  selectionTab: number;
  setSelectionTab: (n: number) => void;
  // Urvals-filter lyfts hit så de överlever stegnavigering (annars nollställs de när Medlemmar-steget avmonteras).
  dueDateFrom: Dayjs | null;
  setDueDateFrom: (d: Dayjs | null) => void;
  dueDateTom: Dayjs | null;
  setDueDateTom: (d: Dayjs | null) => void;
  profileFilters: ProfileFilter[];
  setProfileFilters: Dispatch<SetStateAction<ProfileFilter[]>>;
  excludeProfileFilters: ProfileFilter[];
  setExcludeProfileFilters: Dispatch<SetStateAction<ProfileFilter[]>>;
  // Bekräftade steg (Klar-knapp per kort). I contexten så de överlever stegnavigering och kan sättas av kopiera-modalen.
  dueConfirmed: boolean;
  setDueConfirmed: (b: boolean) => void;
  includeConfirmed: boolean;
  setIncludeConfirmed: (b: boolean) => void;
  excludeConfirmed: boolean;
  setExcludeConfirmed: (b: boolean) => void;
  // Texter-steget: varje sending klar-markeras separat (Klar-kortet blir grönt). I contexten så det överlever stegnavigering.
  emailTextConfirmed: boolean;
  setEmailTextConfirmed: (b: boolean) => void;
  invoiceTextConfirmed: boolean;
  setInvoiceTextConfirmed: (b: boolean) => void;
  // Fakturablankett-fliken (steg 4) klar-markeras separat, precis som texterna, och gatar Fortsätt.
  blankettConfirmed: boolean;
  setBlankettConfirmed: (b: boolean) => void;
  // Förbundets förfallodatum (MMdd) + friperiod (dagar). Hämtas en gång i wizard-sidan och delas av
  // utskicksdatum-panelen och Medlemmar-steget (för att härleda medlemsår ur datum + friperiod).
  membershipMMdd: string | null;
  setMembershipMMdd: (v: string | null) => void;
  gracePeriodDays: number;
  setGracePeriodDays: (n: number) => void;
  // Valt utskicksdatum (SendingDate → Dayjs). Delas så Medlemmar-steget kan härleda medlemsåret ur datum + friperiod.
  sendDate: Dayjs | null;
  setSendDate: (d: Dayjs | null) => void;
  // Texter-stegets flik-navigering (registreras av steget medan det är monterat, annars null).
  textStepNav: TextStepNav | null;
  setTextStepNav: (n: TextStepNav | null) => void;
  // Aviseringen som urvalet kopierades från ("Gör som tidigare avisering"). Används i Texter-steget för att förvälja
  // samma avisering vid "Hämta texter" (och hoppa över väljaren). null = inget urval kopierat.
  copyFromMissionId: number | null;
  setCopyFromMissionId: (id: number | null) => void;
  // Vilket utskicksled wizarden arbetar med: 0 = huvudutskick, 1 = Påminnelse 1, 2 = Påminnelse 2. Stegen läser
  // samma formulärfält oavsett led – fälten laddas om vid ledbyte – men behöver veta ledet för texter och regler.
  led: number;
  setLed: (n: number) => void;
  // Får antalet mottagare visas för det aktiva påminnelseledet? Först när föregående utskicks förfallodatum har
  // passerat säger "obetalda" något – dessförinnan är siffran bara antalet som ännu inte hunnit betala.
  reminderCountsVisible: boolean;
  setReminderCountsVisible: (b: boolean) => void;
  // Föregående utskicks förfallodatum (huvudutskicket för Påminnelse 1, Påminnelse 1 för Påminnelse 2).
  reminderPreviousPayDate: Dayjs | null;
  setReminderPreviousPayDate: (d: Dayjs | null) => void;
  cacheRef: MutableRefObject<SelectionResultCache | null>;
}

const SendingWizardContext = createContext<SendingWizardContextValue | undefined>(undefined);

export const SendingWizardProvider = ({ children }: { children: ReactNode }) => {
  const [coverage, setCoverage] = useState<SelectionCoverage | null>(null);
  // -1 = inget läge valt ännu (ingen lägesknapp förvald i steg 2). 0 = eget urval, 1 = fritext, 2 = kopierat.
  const [selectionTab, setSelectionTab] = useState(-1);
  const [dueDateFrom, setDueDateFrom] = useState<Dayjs | null>(null);
  const [dueDateTom, setDueDateTom] = useState<Dayjs | null>(null);
  const [profileFilters, setProfileFilters] = useState<ProfileFilter[]>([]);
  const [excludeProfileFilters, setExcludeProfileFilters] = useState<ProfileFilter[]>([]);
  const [dueConfirmed, setDueConfirmed] = useState(false);
  const [includeConfirmed, setIncludeConfirmed] = useState(false);
  const [excludeConfirmed, setExcludeConfirmed] = useState(false);
  const [emailTextConfirmed, setEmailTextConfirmed] = useState(false);
  const [invoiceTextConfirmed, setInvoiceTextConfirmed] = useState(false);
  const [blankettConfirmed, setBlankettConfirmed] = useState(false);
  const [membershipMMdd, setMembershipMMdd] = useState<string | null>(null);
  const [gracePeriodDays, setGracePeriodDays] = useState(0);
  const [sendDate, setSendDate] = useState<Dayjs | null>(null);
  const [textStepNav, setTextStepNav] = useState<TextStepNav | null>(null);
  const [copyFromMissionId, setCopyFromMissionId] = useState<number | null>(null);
  const [led, setLed] = useState(0);
  const [reminderCountsVisible, setReminderCountsVisible] = useState(false);
  const [reminderPreviousPayDate, setReminderPreviousPayDate] = useState<Dayjs | null>(null);
  const cacheRef = useRef<SelectionResultCache | null>(null);

  const value = useMemo(
    () => ({
      coverage,
      setCoverage,
      selectionTab,
      setSelectionTab,
      dueDateFrom,
      setDueDateFrom,
      dueDateTom,
      setDueDateTom,
      profileFilters,
      setProfileFilters,
      excludeProfileFilters,
      setExcludeProfileFilters,
      dueConfirmed,
      setDueConfirmed,
      includeConfirmed,
      setIncludeConfirmed,
      excludeConfirmed,
      setExcludeConfirmed,
      emailTextConfirmed,
      setEmailTextConfirmed,
      invoiceTextConfirmed,
      setInvoiceTextConfirmed,
      blankettConfirmed,
      setBlankettConfirmed,
      membershipMMdd,
      setMembershipMMdd,
      gracePeriodDays,
      setGracePeriodDays,
      sendDate,
      setSendDate,
      textStepNav,
      setTextStepNav,
      copyFromMissionId,
      setCopyFromMissionId,
      led,
      setLed,
      reminderCountsVisible,
      setReminderCountsVisible,
      reminderPreviousPayDate,
      setReminderPreviousPayDate,
      cacheRef,
    }),
    [
      coverage,
      selectionTab,
      dueDateFrom,
      dueDateTom,
      profileFilters,
      excludeProfileFilters,
      dueConfirmed,
      includeConfirmed,
      excludeConfirmed,
      emailTextConfirmed,
      invoiceTextConfirmed,
      blankettConfirmed,
      membershipMMdd,
      gracePeriodDays,
      sendDate,
      textStepNav,
      copyFromMissionId,
      led,
      reminderCountsVisible,
      reminderPreviousPayDate,
    ],
  );
  return <SendingWizardContext.Provider value={value}>{children}</SendingWizardContext.Provider>;
};

export const useSendingWizard = (): SendingWizardContextValue => {
  const ctx = useContext(SendingWizardContext);
  if (!ctx) throw new Error('useSendingWizard must be used within SendingWizardProvider');
  return ctx;
};
